/**
 * Helpers for the Tally qualification-form gate before payment.
 * Hidden field names must match those configured in the Tally form.
 */

export const TALLY_HIDDEN_FIELD_EVENT_REGISTRATION_ID = 'event_registration_id';
/** Must match the Tally hidden-field names used as Default answer on the visible questions. */
export const TALLY_HIDDEN_FIELD_NAME = 'name';
export const TALLY_HIDDEN_FIELD_EMAIL = 'email';

export const getRegistrationOptionMeta = (attendee, registrationOptions = []) => {
  const optionId =
    attendee?.registration_option?.event_option_id ??
    attendee?.event_registration_options?.registration?.event_option_id;
  if (!optionId) return null;
  return registrationOptions.find((opt) => opt.id === optionId || opt.value === optionId) ?? null;
};

export const attendeeRequiresQualificationForm = (attendee, registrationOptions = []) => {
  const option = getRegistrationOptionMeta(attendee, registrationOptions);
  return option?.requires_qualification_form === true && !!option?.tally_form_url;
};

export const getPendingQualificationAttendees = (attendees = [], registrationOptions = []) =>
  (attendees ?? []).filter(
    (attendee) =>
      attendeeRequiresQualificationForm(attendee, registrationOptions) &&
      !attendee?.qualification_form_submitted_at,
  );

export const buildTallyFormUrl = (baseUrl, attendee) => {
  if (!baseUrl) return null;
  try {
    const url = new URL(baseUrl);
    const registrationId = String(attendee.id);
    const name = [attendee.attendee_first_name, attendee.attendee_last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    const params = {
      [TALLY_HIDDEN_FIELD_EVENT_REGISTRATION_ID]: registrationId,
      // Some published forms saved the hidden field with a trailing underscore.
      [`${TALLY_HIDDEN_FIELD_EVENT_REGISTRATION_ID}_`]: registrationId,
      [TALLY_HIDDEN_FIELD_NAME]: name,
      [TALLY_HIDDEN_FIELD_EMAIL]: attendee.attendee_email ?? '',
    };
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  } catch {
    return baseUrl;
  }
};

/** Share URLs (`/r/`) become Tally's embeddable form URL, keeping hidden-field params. */
export const toTallyEmbedUrl = (formUrl) => {
  if (!formUrl) return null;
  try {
    const url = new URL(formUrl);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'tally.so') {
      url.pathname = url.pathname.replace(/^\/(r|forms)\//, '/embed/');
    }
    return url.toString();
  } catch {
    return formUrl;
  }
};

export const isTallyFormSubmittedMessage = (event) => {
  try {
    const host = new URL(event?.origin || '').hostname;
    if (host !== 'tally.so' && !host.endsWith('.tally.so')) return false;
  } catch {
    return false;
  }
  const data = event?.data;
  if (typeof data === 'string') return data.includes('Tally.FormSubmitted');
  return data?.event === 'Tally.FormSubmitted';
};
